"use client"

import { MapView } from "@/components/map-view"
import { Button } from "@/components/ui/button"
import { Plus, Waves, Wind, LayoutGrid } from "lucide-react"
import { AddSpotModal } from "@/components/add-spot-modal"
import { useState, useEffect } from "react"
import { useAuth } from "@/lib/auth/context"
import { useRouter } from "next/navigation"
import { ThemeToggle } from "@/components/theme-toggle"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"
import { AppLoadingShell } from "@/components/app-loading-shell"
import type { ActivityType } from "@/lib/types/surf-spot"

export default function MapPage() {
  const [showAddSpotModal, setShowAddSpotModal] = useState(false)
  const [activityFilter, setActivityFilter] = useState<ActivityType | 'all'>('all')
  const { user } = useAuth()
  const { isLoaded, isMobile } = usePlatform()
  const router = useRouter()

  if (!isLoaded) {
    return <AppLoadingShell />
  }

  const handleAddSpotClick = () => {
    triggerHaptic('impact')
    if (!user) {
      router.push("/auth/register")
      return
    }
    setShowAddSpotModal(true)
  }

  const handleFilterChange = (filter: ActivityType | 'all') => {
    triggerHaptic('selection')
    setActivityFilter(filter)
  }

  return (
    <div className="relative w-full h-screen overflow-hidden pt-0 md:pt-0">
      {/* The Map Component */}
      <MapView activityFilter={activityFilter} />

      {/* Floating Filter Bar - Centered at Top */}
      <div className="absolute top-[calc(80px+env(safe-area-inset-top))] md:top-32 left-1/2 -translate-x-1/2 z-30 w-full max-w-[320px] px-4">
        <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl p-1.5 rounded-2xl border border-white/20 dark:border-slate-800 shadow-2xl flex items-center gap-1">
          <button
            onClick={() => handleFilterChange('all')}
            className={`flex-1 flex flex-col items-center justify-center py-2 rounded-xl transition-all ${
              activityFilter === 'all' 
                ? "bg-blue-600 text-white shadow-lg" 
                : "text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
            }`}
          >
            <LayoutGrid className={`w-4 h-4 mb-1 ${activityFilter === 'all' ? "text-white" : "text-slate-400"}`} />
            <span className="text-[9px] font-black uppercase tracking-widest">All</span>
          </button>
          <button
            onClick={() => handleFilterChange('surfing')}
            className={`flex-1 flex flex-col items-center justify-center py-2 rounded-xl transition-all ${
              activityFilter === 'surfing' 
                ? "bg-blue-600 text-white shadow-lg" 
                : "text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
            }`}
          >
            <Waves className={`w-4 h-4 mb-1 ${activityFilter === 'surfing' ? "text-white" : "text-slate-400"}`} />
            <span className="text-[9px] font-black uppercase tracking-widest">Surf</span>
          </button>
          <button
            onClick={() => handleFilterChange('kitesurfing')}
            className={`flex-1 flex flex-col items-center justify-center py-2 rounded-xl transition-all ${
              activityFilter === 'kitesurfing' 
                ? "bg-blue-600 text-white shadow-lg" 
                : "text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
            }`}
          >
            <Wind className={`w-4 h-4 mb-1 ${activityFilter === 'kitesurfing' ? "text-white" : "text-slate-400"}`} />
            <span className="text-[9px] font-black uppercase tracking-widest">Kite</span>
          </button>
        </div>
      </div>

      {/* Floating Add Spot Button - Moved to bottom right corner */}
      <div className="absolute bottom-[calc(24px+64px+env(safe-area-inset-bottom))] right-6 z-30 md:bottom-10 md:right-10">
        <Button
          onClick={handleAddSpotClick}
          size="lg"
          className="rounded-2xl h-14 bg-blue-600 hover:bg-blue-700 text-white shadow-2xl shadow-blue-500/40 flex items-center gap-2 px-6 border-none transition-transform active:scale-95"
        >
          <Plus className="w-5 h-5" />
          <span className="font-black uppercase tracking-widest text-xs">Add Spot</span>
        </Button>
      </div>

      {/* Add Spot Modal */}
      <AddSpotModal open={showAddSpotModal} onOpenChange={setShowAddSpotModal} />
      
      {/* Instructions Overlay for Desktop (Optional) */}
      <div className="hidden md:block absolute top-6 left-6 z-30 pointer-events-none">
        <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-md px-4 py-3 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl">
          <h1 className="text-sm font-black uppercase tracking-widest text-slate-900 dark:text-slate-100">
            Interactive Map
          </h1>
          <p className="text-[10px] font-bold text-blue-500 dark:text-blue-400">
            Explore and add new surf spots
          </p>
        </div>
      </div>
    </div>
  )
}
